import React, { useState, useEffect, useMemo } from 'react';
import { getApiBase } from '../api';

function money(cents) {
  if (cents === null || cents === undefined) return '—';
  return `$${(Number(cents) / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
}

export default function AccountingExports() {
  const apiBase = useMemo(() => getApiBase(), []);
  const [activeTab, setActiveTab] = useState('journal');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [summary, setSummary] = useState(null);
  const [glAccounts, setGlAccounts] = useState(null);
  
  // Date range - default to current month
  const today = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const [startDate, setStartDate] = useState(firstOfMonth.toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(today.toISOString().split('T')[0]);
  
  // Export options
  const [exportFormat, setExportFormat] = useState('csv');
  const [exportType, setExportType] = useState('all');

  useEffect(() => {
    fetchGLAccounts();
  }, []);

  useEffect(() => {
    if (startDate && endDate) {
      fetchSummary();
    }
  }, [startDate, endDate]);

  async function fetchGLAccounts() {
    try {
      const res = await fetch(`${apiBase}/accounting-export/gl-accounts`, {
        headers: { 'x-user-role': 'ACCOUNTING_ADMIN' }
      });
      if (res.ok) {
        const data = await res.json();
        setGlAccounts(data.glAccounts);
      }
    } catch (err) {
      console.error('Failed to fetch GL accounts:', err);
    }
  }

  async function fetchSummary() {
    try {
      const res = await fetch(`${apiBase}/accounting-export/period-summary?startDate=${startDate}&endDate=${endDate}`, {
        headers: { 'x-user-role': 'ACCOUNTING_ADMIN' }
      });
      if (res.ok) {
        setSummary(await res.json());
      }
    } catch (err) {
      console.error('Failed to fetch summary:', err);
    }
  }

  async function downloadExport(endpoint, filename) {
    setLoading(true);
    setMessage(null);
    try {
      const url = `${apiBase}/accounting-export/${endpoint}?startDate=${startDate}&endDate=${endDate}&format=${exportFormat}&type=${exportType}`;
      const res = await fetch(url, {
        headers: { 'x-user-role': 'ACCOUNTING_ADMIN' }
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Export failed');
      }
      
      const blob = await res.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `${filename}_${startDate}_${endDate}.${exportFormat}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(downloadUrl);
      
      setMessage({ type: 'success', text: `Export downloaded: ${filename}` });
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setLoading(false);
    }
  }

  const tabs = [
    { id: 'journal', label: '📒 Journal Entries' },
    { id: 'invoices', label: '📄 Invoices (AR)' },
    { id: 'payments', label: '💳 Carrier Payments (AP)' },
    { id: 'config', label: '⚙️ GL Mapping' }
  ];

  const quickDateRanges = [
    { label: 'This Month', start: firstOfMonth.toISOString().split('T')[0], end: today.toISOString().split('T')[0] },
    { label: 'Last Month', start: new Date(today.getFullYear(), today.getMonth() - 1, 1).toISOString().split('T')[0], end: new Date(today.getFullYear(), today.getMonth(), 0).toISOString().split('T')[0] },
    { label: 'This Quarter', start: new Date(today.getFullYear(), Math.floor(today.getMonth() / 3) * 3, 1).toISOString().split('T')[0], end: today.toISOString().split('T')[0] },
    { label: 'This Year', start: new Date(today.getFullYear(), 0, 1).toISOString().split('T')[0], end: today.toISOString().split('T')[0] },
  ];

  return (
    <div style={{ padding: 20, maxWidth: 1400, margin: '0 auto' }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ margin: 0, fontSize: 24 }}>📤 Accounting Exports</h1>
        <p style={{ margin: '8px 0 0', color: '#6b7280' }}>
          Export data for QuickBooks, SAP, or any accounting system
        </p>
      </div>

      {message && (
        <div style={{
          padding: 12,
          marginBottom: 16,
          borderRadius: 8,
          background: message.type === 'success' ? '#d1fae5' : '#fee2e2',
          color: message.type === 'success' ? '#065f46' : '#991b1b'
        }}>
          {message.text}
          <button onClick={() => setMessage(null)} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer' }}>×</button>
        </div>
      )}

      {/* Date Range Selector */}
      <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e5e7eb', padding: 20, marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
              style={{ padding: 8, borderRadius: 6, border: '1px solid #d1d5db', fontSize: 14 }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={e => setEndDate(e.target.value)}
              style={{ padding: 8, borderRadius: 6, border: '1px solid #d1d5db', fontSize: 14 }}
            />
          </div>
          <div style={{ display: 'flex', gap: 8, marginLeft: 16 }}>
            {quickDateRanges.map(range => (
              <button
                key={range.label}
                onClick={() => { setStartDate(range.start); setEndDate(range.end); }}
                style={{
                  padding: '6px 12px',
                  background: startDate === range.start && endDate === range.end ? '#2563eb' : '#f3f4f6',
                  color: startDate === range.start && endDate === range.end ? 'white' : '#374151',
                  border: 'none',
                  borderRadius: 6,
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                {range.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Period Summary */}
      {summary && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 24 }}>
          <SummaryCard label="Revenue" value={money(summary.profitability.revenueCents)} color="#059669" icon="📈" />
          <SummaryCard label="Carrier Cost" value={money(summary.profitability.costCents)} color="#dc2626" icon="📉" />
          <SummaryCard label="Gross Margin" value={money(summary.profitability.grossMarginCents)} subtext={`${summary.profitability.marginPercent}%`} color="#2563eb" icon="💰" />
          <SummaryCard label="AR Outstanding" value={money(summary.accountsReceivable.outstandingCents)} subtext={`${summary.accountsReceivable.invoiceCount} invoices`} color="#f59e0b" icon="📄" />
          <SummaryCard label="AP Unpaid" value={money(summary.accountsPayable.totalUnpaidCents)} subtext={`${summary.accountsPayable.paymentCount} payments`} color="#7c3aed" icon="💳" />
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, borderBottom: '2px solid #e5e7eb', paddingBottom: 12 }}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '10px 20px',
              background: activeTab === tab.id ? '#374151' : 'white',
              color: activeTab === tab.id ? 'white' : '#374151',
              border: activeTab === tab.id ? 'none' : '1px solid #d1d5db',
              borderRadius: 8,
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Journal Entries Tab */}
      {activeTab === 'journal' && (
        <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e5e7eb', padding: 24 }}>
          <h3 style={{ margin: '0 0 16px' }}>📒 Export Journal Entries</h3>
          <p style={{ color: '#6b7280', marginBottom: 20 }}>
            Generate double-entry journal entries for import into any accounting system.
            Includes invoices, payments received, carrier bills, and payments made.
          </p>

          <div style={{ display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Transaction Type</label>
              <select
                value={exportType}
                onChange={e => setExportType(e.target.value)}
                style={{ padding: 10, borderRadius: 6, border: '1px solid #d1d5db', minWidth: 200 }}
              >
                <option value="all">All Transactions</option>
                <option value="ar">Accounts Receivable Only</option>
                <option value="ap">Accounts Payable Only</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Export Format</label>
              <select
                value={exportFormat}
                onChange={e => setExportFormat(e.target.value)}
                style={{ padding: 10, borderRadius: 6, border: '1px solid #d1d5db', minWidth: 200 }}
              >
                <option value="csv">CSV (Universal)</option>
                <option value="iif">IIF (QuickBooks Desktop)</option>
                <option value="json">JSON (API/Custom)</option>
              </select>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 12 }}>
            <button
              onClick={() => downloadExport('journal-entries', 'journal_entries')}
              disabled={loading}
              style={{
                padding: '12px 24px',
                background: '#2563eb',
                color: 'white',
                border: 'none',
                borderRadius: 8,
                fontWeight: 600,
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.5 : 1,
                fontSize: 15
              }}
            >
              {loading ? '⏳ Generating...' : '📥 Download Journal Entries'}
            </button>
          </div>

          <div style={{ marginTop: 24, padding: 16, background: '#f0fdf4', borderRadius: 8, border: '1px solid #10b981' }}>
            <strong>💡 Import Tips</strong>
            <ul style={{ margin: '8px 0 0', paddingLeft: 20, color: '#065f46', fontSize: 13 }}>
              <li><strong>QuickBooks Online:</strong> Use CSV format, import via Banking → Upload transactions</li>
              <li><strong>QuickBooks Desktop:</strong> Use IIF format, import via File → Utilities → Import</li>
              <li><strong>SAP:</strong> Use CSV, map to FB01/FBV1 posting format</li>
              <li><strong>Xero:</strong> Use CSV, import via Settings → General Ledger</li>
            </ul>
          </div>
        </div>
      )}

      {/* Invoices Tab */}
      {activeTab === 'invoices' && (
        <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e5e7eb', padding: 24 }}>
          <h3 style={{ margin: '0 0 16px' }}>📄 Export Invoices (Accounts Receivable)</h3>
          <p style={{ color: '#6b7280', marginBottom: 20 }}>
            Export shipper invoices for import into your accounting system's AR module.
          </p>

          <div style={{ display: 'flex', gap: 16, marginBottom: 24 }}>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Export Format</label>
              <select
                value={exportFormat}
                onChange={e => setExportFormat(e.target.value)}
                style={{ padding: 10, borderRadius: 6, border: '1px solid #d1d5db', minWidth: 200 }}
              >
                <option value="csv">CSV (Universal)</option>
                <option value="json">JSON (API/Custom)</option>
              </select>
            </div>
          </div>

          <button
            onClick={() => downloadExport('invoices', 'invoices')}
            disabled={loading}
            style={{
              padding: '12px 24px',
              background: '#059669',
              color: 'white',
              border: 'none',
              borderRadius: 8,
              fontWeight: 600,
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.5 : 1,
              fontSize: 15
            }}
          >
            {loading ? '⏳ Generating...' : '📥 Download Invoices'}
          </button>
        </div>
      )}

      {/* Carrier Payments Tab */}
      {activeTab === 'payments' && (
        <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e5e7eb', padding: 24 }}>
          <h3 style={{ margin: '0 0 16px' }}>💳 Export Carrier Payments (Accounts Payable)</h3>
          <p style={{ color: '#6b7280', marginBottom: 20 }}>
            Export carrier payment records for import into your accounting system's AP module.
          </p>

          <div style={{ display: 'flex', gap: 16, marginBottom: 24 }}>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Export Format</label>
              <select
                value={exportFormat}
                onChange={e => setExportFormat(e.target.value)}
                style={{ padding: 10, borderRadius: 6, border: '1px solid #d1d5db', minWidth: 200 }}
              >
                <option value="csv">CSV (Universal)</option>
                <option value="json">JSON (API/Custom)</option>
              </select>
            </div>
          </div>

          <button
            onClick={() => downloadExport('carrier-payments', 'carrier_payments')}
            disabled={loading}
            style={{
              padding: '12px 24px',
              background: '#7c3aed',
              color: 'white',
              border: 'none',
              borderRadius: 8,
              fontWeight: 600,
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.5 : 1,
              fontSize: 15
            }}
          >
            {loading ? '⏳ Generating...' : '📥 Download Carrier Payments'}
          </button>
        </div>
      )}

      {/* GL Mapping Tab */}
      {activeTab === 'config' && (
        <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e5e7eb', padding: 24 }}>
          <h3 style={{ margin: '0 0 16px' }}>⚙️ GL Account Mapping</h3>
          <p style={{ color: '#6b7280', marginBottom: 20 }}>
            These GL account codes are used when generating journal entries. 
            Configure these to match your chart of accounts.
          </p>

          {glAccounts && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 24 }}>
              <div>
                <h4 style={{ margin: '0 0 12px', color: '#059669' }}>Revenue Accounts</h4>
                <GLAccountRow label="Freight Revenue" code={glAccounts.FREIGHT_REVENUE} />
                <GLAccountRow label="Fuel Surcharge Revenue" code={glAccounts.FUEL_SURCHARGE_REVENUE} />
                <GLAccountRow label="Accessorial Revenue" code={glAccounts.ACCESSORIAL_REVENUE} />
              </div>
              <div>
                <h4 style={{ margin: '0 0 12px', color: '#dc2626' }}>Expense Accounts</h4>
                <GLAccountRow label="Carrier Expense" code={glAccounts.CARRIER_EXPENSE} />
                <GLAccountRow label="Fuel Surcharge Expense" code={glAccounts.FUEL_SURCHARGE_EXPENSE} />
                <GLAccountRow label="Factoring Fee" code={glAccounts.FACTORING_FEE_EXPENSE} />
              </div>
              <div>
                <h4 style={{ margin: '0 0 12px', color: '#2563eb' }}>Balance Sheet Accounts</h4>
                <GLAccountRow label="Accounts Receivable" code={glAccounts.ACCOUNTS_RECEIVABLE} />
                <GLAccountRow label="Cash" code={glAccounts.CASH} />
                <GLAccountRow label="Accounts Payable" code={glAccounts.ACCOUNTS_PAYABLE} />
                <GLAccountRow label="Carrier Payable" code={glAccounts.CARRIER_PAYABLE} />
              </div>
            </div>
          )}

          <div style={{ marginTop: 24, padding: 16, background: '#fef3c7', borderRadius: 8, border: '1px solid #f59e0b' }}>
            <strong>⚠️ Note:</strong> GL account customization coming soon. Contact support to configure custom mappings.
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryCard({ label, value, subtext, color, icon }) {
  return (
    <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e5e7eb', padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>{label}</div>
          <div style={{ fontSize: 22, fontWeight: 700, color }}>{value}</div>
          {subtext && <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{subtext}</div>}
        </div>
        <div style={{ fontSize: 24 }}>{icon}</div>
      </div>
    </div>
  );
}

function GLAccountRow({ label, code }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f3f4f6' }}>
      <span style={{ color: '#374151' }}>{label}</span>
      <code style={{ background: '#f3f4f6', padding: '2px 8px', borderRadius: 4, fontSize: 13 }}>{code}</code>
    </div>
  );
}
